/**
 * Invite-only access (GH-871a, DEC-052, owner decisions 2026-10-03). Every shape here exists only
 * while `FEATURE_MEMBERSHIP` is on; with it off every route below answers 404 and every gated
 * route behaves exactly as before.
 *
 *   GET  /v1/public/events/:eventId/display-token        display key  displayTokenResponse (the big screen)
 *   GET  /v1/events/:eventId/display-link                staff    displayLinkResponse (GH-871d)
 *   POST /v1/events/:eventId/display-link/reset          staff    displayLinkResponse (audited; GH-871d)
 *   POST /v1/public/events/access-by-location            none     eventAccessByLocationRequest → …Response
 *   POST /v1/public/waitlist/start                       none     waitlistStartRequest → waitlistStartResponse
 *   POST /v1/public/waitlist/confirm                     none     waitlistConfirmRequest → waitlistConfirmResponse
 *   GET|PUT /v1/admin/membership/access-settings         platform accessSettings (PUT: step-up, audited)
 *   POST /v1/jobs/send-waitlist-admissions               operator {} → { sent, failed, … }
 *   POST /v1/jobs/purge-waitlist-contacts                operator {} → purgeWaitlistContactsResponse
 *
 * Changed while membership is on:
 *
 *   - **Signing up needs an invite.** The four sign-in doors (`/v1/donor/sign-in/confirm`, the
 *     app's Apple door, and the nonce doors for Apple and Google) accept `signUpAccessFields`. A
 *     person with no account and neither field gets `invite_required` (403) and no account is
 *     made; an existing account signs in exactly as before.
 *   - **Events are visible** to a signed-in member, or with an event access token for that event
 *     in the `x-event-access` header (from the big-screen QR's `?access=` or from
 *     `access-by-location`). Otherwise the event page, goal and prompts answer
 *     `event_access_required` (403) and the listings come back empty with `eventsHidden: true`.
 *     The tickets page, ticket orders, giving (`/g/`, donation links, the embed box, pay links,
 *     receipts, pledges) never need either. GH-871d: the display's room state (`presentation`)
 *     is gated like the goal, and also answers a screen paired from the console
 *     (`DISPLAY_KEY_HEADER`) or the event's staff; the token routes answer only a paired screen.
 */
import { z } from "zod";
/* ------------------------------------------------------------------------------- rules */
export const ACCESS_RULES = {
    /** The display asks for a fresh access token at this pace (about every ten minutes). */
    eventAccessRotationSeconds: 600,
    /** An access token is accepted this long after issue, so a scanned page stays open. */
    eventAccessAcceptSeconds: 4 * 60 * 60,
    /** How close to an event's stored position counts as "at the event", by default. */
    defaultEventAccessRadiusMeters: 1500,
    /** The platform setting's bounds (the client rounds a position to ~1 km before sending it). */
    minEventAccessRadiusMeters: 250,
    maxEventAccessRadiusMeters: 10_000,
    /** At most this many open events answer one location check. */
    maxLocationMatches: 3,
    /**
     * A waitlist admission code is usable this long after its wave (owner, 2026-10-03, GH-871d).
     * After that it answers `invite_unavailable`, the same as any code that cannot be used.
     */
    admissionCodeTtlDays: 30,
    /** Account-less waitlist entries are kept this long at most (DEC-011's proposal; GH-871d). */
    waitlistContactRetentionMonths: 13,
};
/** The header an event access token travels in; a URL never carries it to the API. */
export const EVENT_ACCESS_HEADER = "x-event-access";
/** The query parameter the big-screen QR adds to `/c/<eventId>` (read by the web, not the API). */
export const EVENT_ACCESS_QUERY_PARAM = "access";
/** A signed token (`packages/signing/src/event-access-token.ts`), purpose `event_access`. */
export const eventAccessTokenSchema = z
    .string()
    .max(1024)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, "not a signed token");
/**
 * The big-screen auto-invite token, as `membership.ts`'s `eventInviteTokenSchema` spells it —
 * repeated rather than imported because `v1.ts` reads this module and `membership.ts` reads
 * `v1.ts`. `access.test.ts` holds the two to the same answers.
 */
const joinTokenSchema = z
    .string()
    .max(1024)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, "not a signed token");
const timestamp = z.string().datetime({ offset: true });
const uuid = z.string().uuid();
/* ------------------------------------------------------------------- the big screen */
/**
 * GH-871d: the display pairing key. A big screen opened from the console carries it on its link
 * (`?pair=`), keeps it through a reload, strips it from the address bar, and sends it in this
 * header when it asks for the hall's tokens (`display-token`, the older `member-invite-token`) and
 * on its `presentation` reads. Without a valid one, while membership is on, the token routes give
 * no tokens. Only the event's staff (or platform staff) can obtain one.
 */
export const DISPLAY_KEY_HEADER = "x-display-key";
/** The query parameter the console's big-screen link carries (read by the display, not the API). */
export const DISPLAY_KEY_QUERY_PARAM = "pair";
/**
 * `GET /v1/events/:eventId/display-link` (and `POST …/reset`) — the console's big-screen link.
 * `path` is same-origin, so the console opens it on whichever host it is served from:
 * `/e/<eventId>?projector=1`, plus `&pair=<key>` while membership is on. `paired` is false (and
 * `expiresAt` null) while membership is off: the plain link, exactly as before.
 */
export const displayLinkResponseSchema = z
    .object({
    path: z
        .string()
        .max(2048)
        .regex(/^\/e\/[0-9a-f-]{36}\?projector=1(&pair=[A-Za-z0-9_.-]+)?$/, "not a big-screen path"),
    paired: z.boolean(),
    expiresAt: timestamp.nullable(),
})
    .strict();
/**
 * `GET /v1/public/events/:eventId/display-token` — what the hall's QR encodes. The access token is
 * present for every event a donor may see; the auto-invite token only while joining is open (as
 * `member-invite-token` decides). `url` is the check-in page with `?access=` (and `&join=` when
 * there is an invite token). The display also sends the access token on its own goal reads.
 */
export const displayTokenResponseSchema = z
    .object({
    accessToken: eventAccessTokenSchema,
    /** When the access token stops being accepted (issue + `eventAccessAcceptSeconds`). */
    accessExpiresAt: timestamp,
    inviteToken: joinTokenSchema.nullable(),
    inviteExpiresAt: timestamp.nullable(),
    url: z.string().url(),
    refreshAfterSeconds: z.number().int().min(10).max(3600),
})
    .strict();
/* -------------------------------------------------------------------- at the event */
/**
 * The donor's position, already rounded to two decimals by the client (~1 km). Rounded again on
 * arrival, used for one query, and never stored or logged. In the body, so no request log
 * carries it.
 */
export const eventAccessByLocationRequestSchema = z
    .object({
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
})
    .strict();
/**
 * Open events (live, or published and inside their dates — the GH-864h joining window) within the
 * platform radius of the position, nearest first, at most `maxLocationMatches`. Empty when there
 * is none; never an error that says why.
 */
export const eventAccessByLocationResponseSchema = z
    .object({
    events: z
        .array(z
        .object({
        eventId: uuid,
        eventName: z.string(),
        organizationDisplayName: z.string(),
        accessToken: eventAccessTokenSchema,
        accessExpiresAt: timestamp,
    })
        .strict())
        .max(ACCESS_RULES.maxLocationMatches),
})
    .strict();
/* ----------------------------------------------------------------- signing up */
/**
 * Carried on a sign-in request. Either one lets a person with no account create one while
 * membership is on; both are ignored for an existing account (which signs in as before) and with
 * membership off. A code that cannot be used answers `invite_unavailable`; a join token that has
 * expired, or whose event has closed, answers `invite_required`.
 */
export const signUpAccessFields = {
    /** A member's invite code, or the code a waitlist admission sent. Redeemed with the account. */
    inviteCode: z.string().max(32).optional(),
    /** The big-screen QR's `?join=` token. The new account joins as the scan would have. */
    eventJoinToken: joinTokenSchema.optional(),
};
/**
 * What the sign-in made of the new account, on the session response (absent: nothing). GH-884:
 * `ticket` — the app invite a ticket came with, redeemed at sign-up.
 */
export const MEMBERSHIP_JOINED_AT_SIGN_IN = ["invite", "event", "staff", "wave", "ticket"];
/* ------------------------------------------------------- the waitlist, no account */
export const WAITLIST_CHANNELS = ["sms", "email"];
/**
 * `POST /v1/public/waitlist/start` — send a code to a phone or an inbox. One answer whoever the
 * contact belongs to, so the route cannot be used to learn who is on the list or has an account.
 */
export const waitlistStartRequestSchema = z
    .object({
    channel: z.enum(WAITLIST_CHANNELS),
    address: z.string().min(3).max(254),
})
    .strict();
export const waitlistStartResponseSchema = z
    .object({
    verificationId: uuid,
    /** Only where nothing can deliver a code and this is not production. */
    developmentCode: z.string().nullable(),
})
    .strict();
/**
 * `POST /v1/public/waitlist/confirm` — prove the contact; take (or keep) a place in line. No
 * account is created. The answer is for the person who just proved they hold the contact:
 *
 *   - `waiting`: in line at `position` (1-based, people still waiting ahead, signed-in or not);
 *   - `admitted`: already admitted — the invite code has been (or is being) sent to the contact;
 *   - `can_sign_in`: the contact already belongs to an account that can sign in.
 */
export const waitlistConfirmRequestSchema = z
    .object({
    verificationId: uuid,
    code: z.string().min(4).max(10),
    channel: z.enum(WAITLIST_CHANNELS),
    address: z.string().min(3).max(254),
})
    .strict();
export const waitlistConfirmResponseSchema = z
    .object({
    state: z.enum(["waiting", "admitted", "can_sign_in"]),
    position: z.number().int().positive().nullable(),
    waitingDisplay: z.string().nullable(),
})
    .strict()
    .refine((value) => (value.state === "waiting") === (value.position !== null), {
    message: "a position exactly when waiting",
});
/* ---------------------------------------------------------------- platform staff */
export const accessSettingsSchema = z
    .object({
    eventAccessRadiusMeters: z
        .number()
        .int()
        .min(ACCESS_RULES.minEventAccessRadiusMeters)
        .max(ACCESS_RULES.maxEventAccessRadiusMeters),
})
    .strict();
export const updateAccessSettingsRequestSchema = accessSettingsSchema
    .extend({ reason: z.string().trim().min(1).max(500) })
    .strict();
/** `POST /v1/jobs/send-waitlist-admissions`: counts only, never a contact. */
export const sendWaitlistAdmissionsResponseSchema = z
    .object({
    found: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    /** Recorded and not delivered: the purpose is off on this deployment, or a channel refused. */
    notDelivered: z.number().int().nonnegative(),
    /** The provider failed; tried again on the next run. */
    failed: z.number().int().nonnegative(),
    /**
     * GH-871d: the send threw, or an earlier run died mid-send, so whether it went out is unknown.
     * Never sent again (at most once); counted for staff on the membership summary.
     */
    unconfirmed: z.number().int().nonnegative(),
})
    .strict();
/** `POST /v1/jobs/purge-waitlist-contacts` — retention for the account-less waitlist (GH-871d). */
export const purgeWaitlistContactsResponseSchema = z
    .object({
    /** Entries whose code was redeemed: their one job is done. */
    redeemed: z.number().int().nonnegative(),
    /** Entries that joined longer ago than `waitlistContactRetentionMonths`. */
    expired: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=access.js.map